/**
 * Workflow Execution Service
 * Orchestrates the execution of workflow actions on the OptiLogic platform
 */

import { getUserAppKey } from './optilogicService';
import { uploadFile, downloadFile } from './platformStorageService';
import { runJob, monitorJob } from './platformJobService';
import { importCSVToSheet } from './exportDataService';

const WORKFLOW_RUNNER_FILE_NAME = 'workflow_runner_2.py';

/**
 * Read Excel sheet data for workflow
 * Matches VBA behavior: only includes columns with headers in row 1
 * @param {string} sheetName - Name of the sheet to read
 * @returns {Promise<Array<Array>>} 2D array of sheet data
 */
async function readSheetDataForWorkflow(sheetName) {
    return await window.Excel.run(async (context) => {
        const workbook = context.workbook;
        const sheet = workbook.worksheets.getItem(sheetName);

        // Get the full used range first
        const fullUsedRange = sheet.getUsedRange();
        fullUsedRange.load('values, rowCount, columnCount');
        await context.sync();

        const allData = fullUsedRange.values;

        // Find the last non-empty cell in row 1 (header row)
        // This matches VBA: wsExport.Cells(1, ws.Columns.Count).End(xlToLeft).Column
        const headerRow = allData[0];
        let lastColumn = 0;

        for (let col = headerRow.length - 1; col >= 0; col--) {
            if (headerRow[col] !== null && headerRow[col] !== undefined && headerRow[col] !== '') {
                lastColumn = col;
                break;
            }
        }

        // Trim each row to only include columns up to lastColumn
        const trimmedData = allData.map(row => row.slice(0, lastColumn + 1));

        return trimmedData;
    });
}

/**
 * Convert 2D array to CSV string
 * @param {Array<Array>} data - 2D array of data
 * @returns {string} CSV formatted string
 */
function convertArrayToCSV(data) {
    if (!data || data.length === 0) {
        return '';
    }

    const csvRows = data.map(row => {
        return row.map(cell => {
            let cellValue = cell === null || cell === undefined ? '' : String(cell);
            cellValue = cellValue.replace(/"/g, '""');
            if (cellValue.includes(',') || cellValue.includes('\n') || cellValue.includes('"')) {
                cellValue = `"${cellValue}"`;
            }
            return cellValue;
        }).join(',');
    });

    return csvRows.join('\n');
}

/**
 * Build process file content from workflow actions
 * @param {Array<Object>} actions - Array of action objects with {name, type, params}
 * @returns {string} Process file content (pipe-delimited CSV format)
 */
function buildProcessFile(actions) {
    const lines = ['actionParamName|actionParam|actionID|actionType'];

    for (const action of actions) {
        const actionID = action.name;
        const actionType = action.type;

        // Write each parameter as a line
        for (const [paramName, paramValue] of Object.entries(action.params)) {
            // Skip ActionName and ActionType as they're metadata
            if (paramName === 'ActionName' || paramName === 'ActionType') {
                continue;
            }

            lines.push(`${paramName}|${paramValue}|${actionID}|${actionType}`);
        }
    }

    return lines.join('\n');
}

/**
 * Create app.key file content
 * @returns {string} App key content
 */
function buildAppKeyFile() {
    const appKey = getUserAppKey();
    return appKey;
}

/**
 * Get platform folder name from Config sheet
 * @returns {Promise<string>} Platform folder name
 */
async function getPlatformFolderName() {
    return await window.Excel.run(async (context) => {
        const configSheet = context.workbook.worksheets.getItemOrNullObject('Config');
        await context.sync();

        if (configSheet.isNullObject) {
            // Default folder name if Config sheet doesn't exist
            return 'excel_workflow';
        }

        const folderCell = configSheet.getRange('B2');
        folderCell.load('values');
        await context.sync();

        const folderName = folderCell.values[0][0];
        return folderName || 'excel_workflow';
    });
}

/**
 * Get workflow runner.python content from public folder
 * @returns {Promise<string>} Python script content
 */
async function getWorkflowRunnerScript() {
    // Fetch the workflow runner from the public folder
    const response = await fetch(`/${WORKFLOW_RUNNER_FILE_NAME}`);

    if (!response.ok) {
        throw new Error(`Failed to load ${WORKFLOW_RUNNER_FILE_NAME}`);
    }

    return await response.text();
}

/**
 * NOTE: 2/6/2026 - requirements.txt only includes the datastar library.
 * Once the library is added to the platform image, we no longer need to upload the requirements file.
 * 
 * Get requiremments.txt content from public folder
 * 
 * @returns {Promise<string>} text content
 */
async function getRequirementsFile() {
    // Fetch the workflow runner from the public folder
    const response = await fetch('/requirements.txt');

    if (!response.ok) {
        throw new Error('Failed to load requirements.txt');
    }

    return await response.text();
}

/**
 * Execute a single workflow action
 * @param {Object} action - Action object with {name, type, params}
 * @param {Function} onStatusUpdate - Optional callback for status updates
 * @returns {Promise<Object>} Execution result
 */
export async function executeWorkflowAction(action, onStatusUpdate = null) {
    const appKey = getUserAppKey();

    if (!appKey) {
        throw new Error('Not authenticated. Please log in first.');
    }

    const platformFolder = await getPlatformFolderName();

    // Build process file with single action
    const processFileContent = buildProcessFile([action]);
    const appKeyContent = buildAppKeyFile();
    const workflowRunnerScript = await getWorkflowRunnerScript();
    const requirementsFileContent = await getRequirementsFile();

    if (onStatusUpdate) onStatusUpdate('Uploading files to platform...');

    // Upload files to platform
    await uploadFile(platformFolder, 'process.txt', processFileContent);
    await uploadFile(platformFolder, 'app.key', appKeyContent);
    await uploadFile(platformFolder, WORKFLOW_RUNNER_FILE_NAME, workflowRunnerScript);
    await uploadFile(platformFolder, 'requirements.txt', requirementsFileContent);

    if (onStatusUpdate) onStatusUpdate('Starting workflow execution...');

    // Run the job
    const jobKey = await runJob(platformFolder, WORKFLOW_RUNNER_FILE_NAME);

    if (onStatusUpdate) onStatusUpdate('Monitoring job execution...');

    // Monitor until completion
    await monitorJob(jobKey, 5, (status) => {
        if (onStatusUpdate) onStatusUpdate(`Job status: ${status}`);
    });

    if (onStatusUpdate) onStatusUpdate('Job completed successfully');

    // If this was an ExportData action, download the result file
    if (action.type === 'ExportData') {
        const tableName = action.params.CosmicFrogTable;
        const sheetName = action.params.SheetName;

        if (onStatusUpdate) onStatusUpdate(`Downloading ${tableName}.csv...`);

        const csvContent = await downloadFile(platformFolder, `${tableName}.csv`);

        if (onStatusUpdate) onStatusUpdate(`Importing data to sheet "${sheetName}"...`);

        await importCSVToSheet(csvContent, sheetName);

        return {
            success: true,
            message: `Successfully exported ${tableName} to sheet "${sheetName}"`,
            action: 'ExportData'
        };
    }

    return {
        success: true,
        message: `Successfully executed ${action.type} action`,
        action: action.type
    };
}

/**
 * Execute multiple workflow actions in sequence
 * @param {Array<Object>} actions - Array of action objects
 * @param {Function} onStatusUpdate - Optional callback for status updates
 * @returns {Promise<Object>} Execution result
 */
export async function executeWorkflow(actions, onStatusUpdate = null) {
    const appKey = getUserAppKey();

    if (!appKey) {
        throw new Error('Not authenticated. Please log in first.');
    }

    const platformFolder = await getPlatformFolderName();

    // Build process file with all actions
    const processFileContent = buildProcessFile(actions);
    const appKeyContent = buildAppKeyFile();
    const workflowRunnerScript = await getWorkflowRunnerScript();
    const requirementsFileContent = await getRequirementsFile();

    // Debug: Log the process file content
    console.log('Process file content:');
    console.log(processFileContent);

    if (onStatusUpdate) onStatusUpdate('Uploading workflow files to platform...');

    // Upload workflow files to platform
    await uploadFile(platformFolder, 'process.txt', processFileContent);
    await uploadFile(platformFolder, 'app.key', appKeyContent);
    await uploadFile(platformFolder, WORKFLOW_RUNNER_FILE_NAME, workflowRunnerScript);
    await uploadFile(platformFolder, 'requirements.txt', requirementsFileContent);

    // Upload CSV files for ImportData actions
    const importActions = actions.filter(a => a.type === 'ImportData');
    for (const action of importActions) {
        const sheetName = action.params.SheetName;
        if (onStatusUpdate) onStatusUpdate(`Uploading data from sheet "${sheetName}"...`);

        // Read Excel sheet data
        const sheetData = await readSheetDataForWorkflow(sheetName);

        // Convert to CSV
        const csvContent = convertArrayToCSV(sheetData);

        // Upload CSV file (filename must match SheetName + '.csv')
        await uploadFile(platformFolder, `${sheetName}.csv`, csvContent);
    }

    // Upload files for UploadFile actions
    const uploadFileActions = actions.filter(a => a.type === 'UploadFile');
    for (const action of uploadFileActions) {
        const sheetName = action.params.SheetName;
        const fileName = action.params.FileName;
        if (onStatusUpdate) onStatusUpdate(`Uploading file from sheet "${sheetName}" as "${fileName}"...`);

        // Read Excel sheet data
        const sheetData = await readSheetDataForWorkflow(sheetName);

        // Convert to CSV
        const csvContent = convertArrayToCSV(sheetData);

        // Upload file with specified filename
        await uploadFile(platformFolder, fileName, csvContent);
    }

    if (onStatusUpdate) onStatusUpdate('Starting workflow execution...');

    // Run the job
    const jobKey = await runJob(platformFolder, WORKFLOW_RUNNER_FILE_NAME);

    if (onStatusUpdate) onStatusUpdate('Monitoring job execution...');

    // Monitor until completion
    await monitorJob(jobKey, 5, (status) => {
        if (onStatusUpdate) onStatusUpdate(`Job status: ${status}`);
    });

    if (onStatusUpdate) onStatusUpdate('Job completed, processing results...');

    // Download all ExportData results
    const exportActions = actions.filter(a => a.type === 'ExportData');

    for (const action of exportActions) {
        const tableName = action.params.CosmicFrogTable;
        const sheetName = action.params.SheetName;

        if (onStatusUpdate) onStatusUpdate(`Downloading ${tableName}.csv...`);

        const csvContent = await downloadFile(platformFolder, `${tableName}.csv`);

        if (onStatusUpdate) onStatusUpdate(`Importing data to sheet "${sheetName}"...`);

        await importCSVToSheet(csvContent, sheetName);
    }

    // Download all DownloadFile results
    const downloadFileActions = actions.filter(a => a.type === 'DownloadFile');

    for (const action of downloadFileActions) {
        const fileName = action.params.FileName;
        const sheetName = action.params.SheetName || action.params.ImportToSheet;
        console.log(action.params);

        if (onStatusUpdate) onStatusUpdate(`Downloading ${fileName}...`);

        console.log(`Downloading ${fileName}...`);

        const fileContent = await downloadFile(platformFolder, fileName);

        if (onStatusUpdate) onStatusUpdate(`Importing file to sheet "${sheetName}"...`);

        console.log(`Importing file to sheet "${sheetName}"...`);

        await importCSVToSheet(fileContent, sheetName);
    }

    if (onStatusUpdate) onStatusUpdate('Workflow completed successfully');

    return {
        success: true,
        message: `Successfully executed ${actions.length} action(s)`,
        actionsProcessed: actions.length,
        exportsProcessed: exportActions.length
    };
}
